"use client";

import type { MeasurementServicePricingConfig } from "@/lib/pricing";
import { useTranslations } from "next-intl";
import { ComponentProps, useEffect, useState } from "react";
import Stepper from "../../Stepper";
import MainLink from "../navigation/MainLink";
import AddOptions from "./AddOptions";
import ChooseProperty from "./ChooseProperty";
import CountUnits from "./CountUnits";
import Result from "./Result";

type PropertyType = "multiFamilyHouse" | "singleFamilyHouse";

const selectedOptions: Record<string, string> = {
  extraLivCalc: "extraLivCalc",
  cut: "cut",
  no: "no",
  yes: "yes",
  oneView: "oneView",
  forThreeMonth: "forThreeMonth",
  twoViews: "twoViews",
  forSixMonth: "forSixMonth",
  threeViews: "threeViews",
  fourViews: "fourViews",
};

interface PriceCalculatorStepsProps {
  orderHref?: ComponentProps<typeof MainLink>["href"];
  pricingConfig?: MeasurementServicePricingConfig;
}

const PriceCalculatorSteps = ({
  orderHref = "/user/order-measurement-service",
  pricingConfig,
}: PriceCalculatorStepsProps) => {
  const tC = useTranslations("common");

  const [propertyType, setPropertyType] =
    useState<PropertyType>("singleFamilyHouse");
  const [floors, setFloors] = useState([1]);
  const [squareMeter, setSquareMeter] = useState([15]);
  const [livingUnits, setLivingUnits] = useState([1]);
  const [
    additionalLivingSpaceCalculation,
    setAdditionalLivingSpaceCalculation,
  ] = useState(false);
  const [cutNeeded, setCutNeeded] = useState(false);
  const [dwgFile, setDwgFile] = useState(false);
  const [extraViews, setExtraViews] = useState("no");
  const [matterport, setMatterport] = useState("no");
  const [options, setOptions] = useState<string[]>([]);

  useEffect(() => {
    const newOptions = [
      additionalLivingSpaceCalculation ? "extraLivCalc" : "",
      cutNeeded ? "cut" : "",
      dwgFile ? "dwgFile" : "",
      selectedOptions[extraViews],
      selectedOptions[matterport],
    ];
    setOptions(newOptions.filter((option) => option !== "no" && option !== ""));
  }, [
    additionalLivingSpaceCalculation,
    cutNeeded,
    dwgFile,
    extraViews,
    matterport,
  ]);

  useEffect(() => {
    if (propertyType === "singleFamilyHouse" && livingUnits[0] !== 1) {
      setLivingUnits([1]);
    }
  }, [livingUnits, propertyType, setLivingUnits]);

  return (
    <div className="mdx:w-10/12 bg-card m-auto w-full shadow-lg">
      {/* Stepper */}
      <Stepper
        steps={[
          {
            label: tC("property"),
            children: (
              <ChooseProperty
                propertyType={propertyType}
                setPropertyType={setPropertyType}
              />
            ),
          },
          {
            label: tC("basicData"),
            children: (
              <CountUnits
                floors={floors}
                setFloors={setFloors}
                squareMeter={squareMeter}
                setSquareMeter={setSquareMeter}
                livingUnits={livingUnits}
                setLivingUnits={setLivingUnits}
                propertyType={propertyType}
              />
            ),
          },
          {
            label: tC("options"),
            children: (
              <AddOptions
                additionalLivingSpaceCalculation={
                  additionalLivingSpaceCalculation
                }
                setAdditionalLivingSpaceCalculation={
                  setAdditionalLivingSpaceCalculation
                }
                cutNeeded={cutNeeded}
                setCutNeeded={setCutNeeded}
                extraViews={extraViews}
                setExtraViews={setExtraViews}
                matterport={matterport}
                setMatterport={setMatterport}
                dwgFile={dwgFile}
                setDwgFile={setDwgFile}
              />
            ),
          },
          {
            label: tC("total"),
            children: (
              <Result
                options={options}
                propertyType={propertyType}
                floors={floors[0]}
                squareMeter={squareMeter[0]}
                livingUnits={livingUnits[0]}
                additionalLivingSpaceCalculation={
                  additionalLivingSpaceCalculation
                }
                cutNeeded={cutNeeded}
                dwgFile={dwgFile}
                extraViews={extraViews}
                matterport={matterport}
                pricingConfig={pricingConfig}
                orderHref={orderHref}
              />
            ),
          },
        ]}
      />
    </div>
  );
};

export default PriceCalculatorSteps;
